import { formatTenne } from "@awtoban/shared";
import * as React from "react";
import { useTranslation } from "react-i18next";

interface LineItem {
  kind: string;
  rate_tenne_per_km: number | string;
  distance_km: number | string;
  amount_tenne: number;
}

interface Quote {
  line_items: LineItem[];
  subtotal_tenne: number;
  permit_fee_tenne: number;
  total_tenne: number;
}

function localeFromI18n(lang: string): string {
  if (lang === "en") return "en-US";
  if (lang === "ru") return "ru-RU";
  return "tk-TM";
}

export function LineItemsPanel({
  quote,
  status,
}: {
  quote: Quote | undefined;
  status: "idle" | "distance_missing" | "not_provisioned" | "loading" | "ready" | "stale" | "error";
}) {
  const { t, i18n } = useTranslation();
  const locale = localeFromI18n(i18n.language);

  if (status === "not_provisioned") {
    return (
      <div className="p-6">
        <div className="rounded-md border border-line-strong bg-paper-soft p-4">
          <div className="font-semibold">{t("not_provisioned.title")}</div>
          <p className="mt-1 text-ink-muted text-sm">{t("not_provisioned.body")}</p>
        </div>
      </div>
    );
  }

  if (status === "distance_missing") {
    return (
      <div className="p-6">
        <div className="rounded-md border border-warning bg-warning/10 p-4 text-warning-fg">
          <div className="font-semibold">{t("distance_missing.title")}</div>
          <p className="mt-1 text-sm">{t("distance_missing.body")}</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="p-6">
        <div className="rounded-md border border-danger bg-danger/10 p-4 text-danger">
          <div className="font-semibold">{t("quote.error_title")}</div>
          <p className="mt-1 text-sm">{t("quote.error_body")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="mb-4 font-medium text-ink-muted text-xs uppercase tracking-wider">
        {t("quote.itemized_cost")}
      </h2>

      <div className="space-y-1">
        {quote?.line_items
          ? quote.line_items
              .filter((li) => li.amount_tenne > 0)
              .map((li) => <LineRow key={li.kind} item={li} locale={locale} />)
          : Array.from({ length: 6 }, (_, i) => i).map((i) => <LineRowSkeleton key={i} />)}
      </div>

      <div className="mt-6 space-y-1 border-line-strong border-t pt-4">
        <SubRow label={t("quote.subtotal")} amount={quote?.subtotal_tenne} locale={locale} />
      </div>

      <div className="mt-6 flex items-baseline justify-between rounded-md border border-accent bg-accent-soft px-4 py-3">
        <span className="font-medium text-accent text-xs uppercase tracking-wider">
          {t("quote.total")}
        </span>
        <span className="tabular font-semibold text-3xl text-accent">
          {quote ? `${formatTenne(quote.total_tenne, locale)} TMT` : "—"}
        </span>
      </div>

      {status === "loading" || status === "stale" ? (
        <div className="mt-4 text-ink-muted text-xs">{t("quote.calculating")}</div>
      ) : null}
    </div>
  );
}

function LineRow({ item, locale }: { item: LineItem; locale: string }) {
  const { t } = useTranslation();
  const label = t(`quote.${item.kind}`);
  const showRate = item.kind !== "permit";
  return (
    <div className="grid grid-cols-[1fr_auto] items-baseline gap-3 py-1">
      <div>
        <div className="text-[13px]">{label}</div>
        {showRate ? (
          <div className="tabular text-[11px] text-ink-subtle">
            {t("quote.rate_per_km", {
              rate: item.rate_tenne_per_km,
              distance: item.distance_km,
            })}
          </div>
        ) : null}
      </div>
      <div className="tabular text-[15px]">{formatTenne(item.amount_tenne, locale)}</div>
    </div>
  );
}

function LineRowSkeleton() {
  return (
    <div className="grid grid-cols-[1fr_auto] items-baseline gap-3 py-2">
      <div className="h-3 w-3/4 rounded bg-paper-soft" />
      <div className="h-3 w-16 rounded bg-paper-soft" />
    </div>
  );
}

function SubRow({
  label,
  amount,
  locale,
}: {
  label: string;
  amount: number | undefined;
  locale: string;
}) {
  return (
    <div className="grid grid-cols-[1fr_auto] items-baseline gap-3">
      <span className="text-[13px] text-ink-muted">{label}</span>
      <span className="tabular text-[14px]">
        {amount != null ? formatTenne(amount, locale) : "—"}
      </span>
    </div>
  );
}

const _memo = React.memo(LineItemsPanel);
export default _memo;
